import type { ChannelEpgData, ProgramEpgData } from '@norlys-a-s/norlys-play-kmm';
import { TvGuideChannel, TvGuideProgram } from '@smart-tv/pages/tv-guide/types';
import getTimeFormatted from '@smart-tv/utils/date/get-time-formatted';
import getUnixDate from '@smart-tv/utils/date/get-unix-date';
import getUnixTime from '@smart-tv/utils/date/get-unix-time';

import { getFavoriteListIdentifierForKmm, kmm } from './kmm';

interface Response {
  channels?: TvGuideChannel[];
}

const mapProgram = (
  program: ProgramEpgData,
  sid: number,
  date: Date,
  programIndex: number,
  totalPrograms: number
): TvGuideProgram => {
  const startTime = getUnixDate(program.startTimeEpochSeconds);
  const endTime = getUnixDate(program.endTimeEpochSeconds);
  const startTimeFormatted = getTimeFormatted(startTime);
  const endTimeFormatted = getTimeFormatted(endTime);

  const startTimeInMinutes =
    startTime.getFullYear() !== date.getFullYear() ||
    startTime.getMonth() !== date.getMonth() ||
    startTime.getDate() !== date.getDate()
      ? 0
      : startTime.getHours() * 60 + startTime.getMinutes();
  const endTimeInMinutes =
    endTime.getFullYear() !== date.getFullYear() ||
    endTime.getMonth() !== date.getMonth() ||
    endTime.getDate() !== date.getDate()
      ? 24 * 60
      : endTime.getHours() * 60 + endTime.getMinutes();

  return {
    sid,
    eventId: program.eventId,
    seriesId: program.seriesId,
    programId: program.programId,
    title: program.title,
    imageUrl: program.programImage || '',
    startTime: program.startTimeEpochSeconds,
    endTime: program.endTimeEpochSeconds,
    startTimeInMinutes,
    endTimeInMinutes,
    duration: program.endTimeEpochSeconds - program.startTimeEpochSeconds,
    durationInMinutes: endTimeInMinutes - startTimeInMinutes,
    isFavorite: program.isCustomerFavorite,
    period: `${startTimeFormatted} - ${endTimeFormatted}`,
    catchupTimeout: program.catchupTimeoutEpochSeconds,
    isLastProgram: programIndex === totalPrograms - 1,
    trickModes: program.trickmodes.bitmask,
    genre: program.genre?.name || '',
    year: program.year || '',
    recording: program.npvrInfo?.isCustomerRecording ?? false
  };
};

const responseMapper = (channels: ReadonlyArray<ChannelEpgData>, date: Date): Response => {
  const mappedChannels: TvGuideChannel[] = channels.map((channel) => {
    const sid = channel.sid;
    const programs = Array.from(channel.epgData.asJsReadonlyArrayView()).map(
      (program, index, allPrograms) => mapProgram(program, sid, date, index, allPrograms.length)
    );

    return {
      sid,
      name: channel.channelName,
      icon: channel.channelIcon,
      isInNpvr: channel.channelIsInCustomerNpvrPackage,
      programs
    };
  });

  return {
    channels: mappedChannels.length ? mappedChannels : undefined
  };
};

export const getEpgDataForChannels = async (date: Date, favoriteList = -2): Promise<Response> => {
  const result = await kmm((repo) =>
    repo.getEpgDataForChannels(
      getUnixTime(date),
      getFavoriteListIdentifierForKmm(favoriteList),
      null
    )
  );

  return responseMapper(Array.from(result?.asJsReadonlyArrayView()), date);
};

export default getEpgDataForChannels;
